// Castintech dashboard — Server-Sent Events client modulu (TypeScript).
// Faz 9b PR5 — connectSse, disconnectSse, fallback + visibilitychange.
// Faz 12 PR-D — sse.js → sse.ts (event listener'lar typed).
// Faz 18 PR-D — backend simetrisi: 3 emitter (recordLongRunning,
// updateLongRunningStatus, recordInvoice) artik paired auditLog() cagiriyor.
// PR-A'daki synthetic-row hack silindi; 'audit.new' handler'i 5 specialized
// title'i da otomatik tasiyor (long-running-start/status, invoice-created).
// Saglik.ts title-bazli renk/badge zaten bu title'lari taniyor.

import { state } from './state.js';
import {
  AUDIT_TRIM_BASE,
  SSE_FALLBACK_THRESHOLD,
  SSE_FALLBACK_INTERVAL_MS,
} from './config.js';
import { renderAudit, loadAudit } from './views/saglik.js';
import { renderTelegram, loadTelegramSessions } from './views/telegram.js';
import { loadMusteriler, invalidateOpenDrawer } from './views/musteriler.js';
import { loadFaturalar } from './views/faturalar.js';
import { loadKararlar } from './views/kararlar.js';
import { loadSkillUsage } from './views/skills.js';
// Faz 44 PR-B — API Keys admin paneli SSE invalidation.
import { loadApiKeys } from './views/api-keys.js';
// WP3 dilim 5 — omnichannel gelen kutusu canlı yenileme.
import { gelenKutusuCanliOlay } from './views/gelen-kutusu.js';
import type { AuditRow, TelegramSession } from '../types/api';

export function connectSse(): void {
  if (state.sseConn) return;
  try {
    const es = new EventSource('/castintech-api/events');
    state.sseConn = es;
    es.addEventListener('open', () => {
      state.sseErrorCount = 0;
      stopSseFallback();
    });
    es.addEventListener('audit.new', (ev) => {
      try {
        const row = JSON.parse((ev as MessageEvent).data) as AuditRow;
        // Faz 22 PR-C — Kararlar view: customer-decision audit row'u → invalidate.
        // Filter sıralaması: invalidation triggering filter atamasından ÖNCE
        // (saglik filtersi eşleşmezse return ederse karar invalidation kaybolurdu).
        if (row.title === 'customer-decision' && state.currentPage === 'kararlar') {
          loadKararlar();
        }
        // Faz 23 PR-B — Skill kullanım telemetrisi: skill-invoked audit row
        // skills view açıkken usage panel'i invalidate.
        if (row.title === 'skill-invoked' && state.currentPage === 'skills') {
          loadSkillUsage();
        }
        if (state.auditFilter && row.title !== state.auditFilter) return;
        if (state.auditActorFilter && row.actor !== state.auditActorFilter) return;
        if (state.auditActionFilter && row.action !== state.auditActionFilter) return;
        state.auditRows.unshift(row);
        // Faz 8b — pagination ile genisletilmis feed'i trim'le kuculttume;
        // mevcut uzunlugu koru, sadece base + 1'i geceni kirp.
        const trimLimit = Math.max(AUDIT_TRIM_BASE, state.auditRows.length - 1);
        if (state.auditRows.length > trimLimit) state.auditRows.length = trimLimit;
        if (row.actor && !state.auditActors.includes(row.actor)) state.auditActors.push(row.actor);
        if (row.action && !state.auditActions.includes(row.action)) state.auditActions.push(row.action);
        if (row.title && !state.auditTitles.includes(row.title)) state.auditTitles.push(row.title);
        if (state.currentPage === 'saglik') renderAudit();
      } catch (e) {
        console.error('audit.new parse:', e);
      }
    });
    es.addEventListener('telegram.session.new', (ev) => {
      try {
        const row = JSON.parse((ev as MessageEvent).data) as TelegramSession;
        state.telegramSessions.unshift(row);
        if (state.currentPage === 'telegram') renderTelegram();
      } catch (e) {
        console.error('telegram.session.new parse:', e);
      }
    });
    es.addEventListener('health.ping', () => {
      state.sseErrorCount = 0;
    });
    // Faz 21 PR-B — lifecycle.transition (Faz 18 PR-D backend'den) →
    // Müşteriler kanban view açıkken canlı invalidate. Açık değilse no-op
    // (navigate sırasında loadMusteriler zaten fresh çekecek).
    es.addEventListener('lifecycle.transition', (ev) => {
      if (state.currentPage === 'musteriler') loadMusteriler();
      // Faz 42 PR-E — açık drawer aynı slug ise re-fetch.
      try {
        const data = JSON.parse((ev as MessageEvent).data || '{}') as { client_slug?: string };
        invalidateOpenDrawer(data.client_slug);
      } catch { /* parse fail OK */ }
    });
    // Faz 42 PR-A/E — proje state machine event'leri: kanban dashboard yok
    // ama detay drawer Projeler tab'ı bu event'lerle invalidate edilir.
    es.addEventListener('project.created', (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || '{}') as { client_slug?: string };
        invalidateOpenDrawer(data.client_slug);
      } catch { /* parse fail OK */ }
    });
    es.addEventListener('project.transition', (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent).data || '{}') as { client_slug?: string };
        invalidateOpenDrawer(data.client_slug);
      } catch { /* parse fail OK */ }
    });
    // Faz 22 PR-B — invoice.created + invoice.status (Faz 18 PR-D backend'den) →
    // Faturalar timeline view açıkken canlı invalidate. Açık değilse no-op
    // (navigate sırasında loadFaturalar zaten fresh çekecek).
    es.addEventListener('invoice.created', () => {
      if (state.currentPage === 'faturalar') loadFaturalar();
    });
    es.addEventListener('invoice.status', () => {
      if (state.currentPage === 'faturalar') loadFaturalar();
    });
    // Faz 44 PR-B — API Keys panel SSE invalidation. Backend POST/PATCH/DELETE
    // endpoint'leri 'api-credential.created/updated/revoked' broadcast eder;
    // panel açıkken canlı re-fetch (kapalıysa no-op, navigate'te fresh fetch).
    es.addEventListener('api-credential.created', () => {
      if (state.currentPage === 'api-keys') loadApiKeys();
    });
    es.addEventListener('api-credential.updated', () => {
      if (state.currentPage === 'api-keys') loadApiKeys();
    });
    es.addEventListener('api-credential.revoked', () => {
      if (state.currentPage === 'api-keys') loadApiKeys();
    });
    // WP3 dilim 5 — omnichannel: yeni gelen mesaj / personel değişikliği. Olay yalnız kimlik taşır; gelen kutusu
    // açıksa liste ve açık konuşma kendi uçlarından yeniden okunur (yazılmakta olan metin korunur).
    for (const olayAdi of ['omni.message.new', 'omni.conversation.updated']) {
      es.addEventListener(olayAdi, (ev) => {
        if (state.currentPage !== 'gelen-kutusu') return;
        try {
          gelenKutusuCanliOlay(JSON.parse((ev as MessageEvent).data || '{}') as { konusmaId?: unknown });
        } catch { /* bozuk olay yok sayılır */ }
      });
    }
    es.addEventListener('error', () => {
      state.sseErrorCount++;
      if (state.sseErrorCount >= SSE_FALLBACK_THRESHOLD) startSseFallback();
    });
  } catch (e) {
    console.error('connectSse:', e);
    startSseFallback();
  }
}

export function disconnectSse(): void {
  if (state.sseConn) {
    try { state.sseConn.close(); } catch {}
    state.sseConn = null;
  }
}

export function startSseFallback(): void {
  if (state.sseFallbackTimer) return;
  state.sseFallbackTimer = setInterval(() => {
    if (state.currentPage === 'saglik') loadAudit();
    if (state.currentPage === 'telegram') loadTelegramSessions();
  }, SSE_FALLBACK_INTERVAL_MS);
}

export function stopSseFallback(): void {
  if (state.sseFallbackTimer) {
    clearInterval(state.sseFallbackTimer);
    state.sseFallbackTimer = null;
  }
}

// Tab gizlendiginde SSE'yi kapat, geri donduğünde yeniden bagla.
// Modul yuklendiginde bir kere bind edilir.
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    disconnectSse();
  } else {
    connectSse();
  }
});
